import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { adminUrl } from '../lib/apps';
import { enumLabel, useI18n } from '../lib/i18n';
import { formatDate, formatMoney } from '../lib/format';
import { ProjectContractsCard } from './Contracts';
import { ProjectSubscriptionsCard } from './Subscriptions';
import { P, useAuth } from '../lib/auth';
import {
  Badge,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  LoadingBlock,
  Metric,
  PageHeader,
  Table,
  Td,
  Th,
  Tr,
  statusTone,
} from '../components/ui';

/**
 * CR-001 §33 — a project's money.
 *
 * Projects are run in the admin; this was the Finance tab on the admin's
 * project page. A project has no payments of its own — the chain is project →
 * invoices → payments — so this shows the invoices and the base-currency total
 * actually collected against them.
 *
 * Route: `/projects/:id`, reached from an invoice, a client's projects and the
 * dashboard's collections by project.
 */
export function ProjectMoneyPage() {
  const { id = '' } = useParams();
  const { t, locale, localized } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['project-financials', id],
    queryFn: () => api.get<any>(`/projects/${id}/financials`),
  });

  if (isLoading) return <LoadingBlock />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;
  if (!data) return null;

  const { project } = data;
  const client = project.client;

  return (
    <>
      <div className="mb-4">
        {client ? (
          <Link
            to={`/clients/${client.id}`}
            className="inline-flex items-center gap-1.5 text-body-sm text-muted hover:text-[var(--text-primary)]"
          >
            <ArrowLeft className="h-4 w-4 flip-rtl" aria-hidden />
            {locale === 'ar' && client.companyNameAr ? client.companyNameAr : client.companyName}
          </Link>
        ) : (
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1.5 text-body-sm text-muted hover:text-[var(--text-primary)]"
          >
            <ArrowLeft className="h-4 w-4 flip-rtl" aria-hidden />
            {t('common.back')}
          </button>
        )}
      </div>

      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-3">
            <span className="font-mono text-h2 text-primary-600 dark:text-primary-400">{project.code}</span>
            <span>{localized(project)}</span>
          </span>
        }
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            {t('projectMoney.title')}
            {client ? (
              <Badge tone="brand">{client.code}</Badge>
            ) : (
              <Badge tone="neutral">{t('clients.internalProject')}</Badge>
            )}
          </span>
        }
        action={
          <a
            href={`${adminUrl()}/projects/${project.id}`}
            className="inline-flex items-center gap-1.5 text-body-sm text-muted hover:text-[var(--text-primary)]"
          >
            <ExternalLink className="h-4 w-4 flip-rtl" aria-hidden />
            {t('projectMoney.openInAdmin')}
          </a>
        }
      />

      <div className="grid gap-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <Metric
            label={t('invoices.collectedLocal')}
            value={formatMoney(data.collectedLocalAmount, data.baseCurrency, locale)}
            tone="success"
          />
          <Metric label={t('nav.invoices')} value={data.invoices.length} />
        </div>

        {data.byCurrency.length > 0 && (
          <Card padded={false}>
            <CardHeader title={t('invoices.byCurrency')} className="p-5 pb-0" />
            <Table>
              <thead>
                <tr>
                  <Th>{t('common.currency')}</Th>
                  <Th align="end">{t('reports.invoiced')}</Th>
                  <Th align="end">{t('invoices.paid')}</Th>
                  <Th align="end">{t('clients.totalOutstanding')}</Th>
                </tr>
              </thead>
              <tbody>
                {data.byCurrency.map((row: any) => (
                  <Tr key={row.currency}>
                    <Td><span className="font-mono text-mono-sm">{row.currency}</span></Td>
                    <Td align="end" className="tabular">{formatMoney(row.invoiced, row.currency, locale)}</Td>
                    <Td align="end" className="tabular text-success-fg dark:text-success">{formatMoney(row.paid, row.currency, locale)}</Td>
                    <Td align="end" className={row.outstanding > 0 ? 'tabular text-warning-fg dark:text-warning-dark' : 'tabular'}>
                      {formatMoney(row.outstanding, row.currency, locale)}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </Card>
        )}

        <Card padded={false}>
          <CardHeader title={t('nav.invoices')} className="p-5 pb-0" />
          {!data.invoices.length ? (
            <EmptyState />
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>{t('tickets.number')}</Th>
                  <Th>{t('common.status')}</Th>
                  <Th>{t('invoices.dueDate')}</Th>
                  <Th align="end">{t('common.total')}</Th>
                  <Th align="end">{t('invoices.remaining')}</Th>
                </tr>
              </thead>
              <tbody>
                {data.invoices.map((inv: any) => (
                  <Tr key={inv.id} onClick={() => navigate(`/invoices/${inv.id}`)}>
                    <Td><span className="font-mono text-mono-sm text-primary-600 dark:text-primary-400">{inv.number}</span></Td>
                    <Td><Badge tone={statusTone(inv.status)}>{enumLabel(inv.status, locale)}</Badge></Td>
                    <Td className="tabular text-muted">{formatDate(inv.dueDate, locale)}</Td>
                    <Td align="end" className="tabular">{formatMoney(inv.totalAmount, inv.currency, locale)}</Td>
                    <Td
                      align="end"
                      className={inv.remainingAmount > 0 ? 'tabular text-warning-fg dark:text-warning-dark' : 'tabular text-success-fg dark:text-success'}
                    >
                      {formatMoney(inv.remainingAmount, inv.currency, locale)}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>

        <ProjectContractsCard projectId={project.id} clientId={client?.id ?? null} />
        {/* An internal project Finance may sell by subscription (2026-10-28). */}
        {!client && can(P.SUBSCRIPTIONS_VIEW) && <ProjectSubscriptionsCard projectId={project.id} />}
      </div>
    </>
  );
}
